import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchProgramVersions, createProgramVersionService } from '../services/program-version.service';
import { ProgramVersionDto, CreateProgramVersionDto, UpdateProgramVersionDto } from '../types/dtos/program';
import { PaginationParams } from '../types/common';

const PROGRAM_VERSIONS_KEY = 'program-versions';

export const useProgramVersions = () => {
  return useQuery<ProgramVersionDto[], Error>({
    queryKey: [PROGRAM_VERSIONS_KEY],
    queryFn: fetchProgramVersions,
  });
};

export const useProgramVersionsByProgram = (
  organizationId: string,
  programId: string,
  params?: PaginationParams & { isActive?: boolean }
) => {
  const service = createProgramVersionService(organizationId, programId);

  return useQuery({
    queryKey: [PROGRAM_VERSIONS_KEY, organizationId, programId, params],
    queryFn: () => service.findAll(params).then((res) => res.data),
    enabled: !!organizationId && !!programId,
  });
};

export const useProgramVersion = (
  organizationId: string,
  programId: string,
  versionId: string
) => {
  const service = createProgramVersionService(organizationId, programId);

  return useQuery({
    queryKey: [PROGRAM_VERSIONS_KEY, organizationId, programId, versionId],
    queryFn: () => service.findById(versionId).then((res) => res.data),
    enabled: !!organizationId && !!programId && !!versionId,
  });
};

export const useCreateProgramVersion = (
  organizationId: string,
  programId: string
) => {
  const queryClient = useQueryClient();
  const service = createProgramVersionService(organizationId, programId);

  return useMutation({
    mutationFn: (data: CreateProgramVersionDto) => 
      service.create(data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROGRAM_VERSIONS_KEY, organizationId, programId] });
      queryClient.invalidateQueries({ queryKey: ['allProgramVersionsDetailed', organizationId] });
    }
  });
};

export const useUpdateProgramVersion = (
  organizationId: string,
  programId: string
) => {
  const queryClient = useQueryClient();
  const service = createProgramVersionService(organizationId, programId);

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProgramVersionDto }) =>
      service.update(id, data).then((res) => res.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [PROGRAM_VERSIONS_KEY, organizationId, programId] });
      queryClient.invalidateQueries({ queryKey: [PROGRAM_VERSIONS_KEY, organizationId, programId, id] });
      queryClient.invalidateQueries({ queryKey: ['allProgramVersionsDetailed', organizationId] });
    }
  });
};

export const useDeleteProgramVersion = (
  organizationId: string,
  programId: string
) => {
  const queryClient = useQueryClient();
  const service = createProgramVersionService(organizationId, programId);

  return useMutation({
    mutationFn: (id: string) => service.delete(id).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROGRAM_VERSIONS_KEY, organizationId, programId] });
      queryClient.invalidateQueries({ queryKey: ['allProgramVersionsDetailed', organizationId] });
    }
  });
}; 